export interface PermissionView {
  [key: string]: unknown;
  key?: unknown;
  labelName?: unknown;
  viewName?: unknown;
  webRoute?: unknown;
  icon?: unknown;
  permission: string[];
  subview: PermissionView[];
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function parseEstablishmentId(value: unknown): number | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const establishmentId = Number(value);
  return Number.isInteger(establishmentId) && establishmentId > 0
    ? establishmentId
    : null;
}

function permissionIsActive(validUntil: unknown, nowMs: number): boolean {
  if (validUntil === null || validUntil === undefined || validUntil === '') {
    return true;
  }

  // Accept both JavaScript milliseconds and the Unix seconds commonly emitted
  // by APIs. Contemporary millisecond timestamps are safely above this bound.
  const expiresAt = typeof validUntil === 'number'
    ? (Math.abs(validUntil) < 1_000_000_000_000 ? validUntil * 1000 : validUntil)
    : Date.parse(String(validUntil));

  return Number.isFinite(expiresAt) && expiresAt > nowMs;
}

/**
 * Converts both API permission formats (`string` and `{ name, validUntil }`)
 * into the string list consumed by page-level permission checks.
 */
export function normalizePermissionNames(value: unknown, nowMs = Date.now()): string[] {
  const names = asArray(value).flatMap((permission): string[] => {
    if (typeof permission === 'string') {
      const name = permission.trim();
      return name ? [name] : [];
    }

    const record = asRecord(permission);
    const name = typeof record?.['name'] === 'string' ? record['name'].trim() : '';
    return name && permissionIsActive(record?.['validUntil'], nowMs) ? [name] : [];
  });

  return [...new Set(names)];
}

function normalizeView(value: unknown, nowMs: number): PermissionView | null {
  const view = asRecord(value);
  if (!view) return null;

  return {
    ...view,
    permission: normalizePermissionNames(view['permission'], nowMs),
    subview: normalizeViewTree(view['subview'], nowMs),
  };
}

function viewIdentity(view: PermissionView): string | null {
  for (const field of ['key', 'webRoute', 'viewName']) {
    const value = view[field];
    if (typeof value === 'number' || (typeof value === 'string' && value.trim())) {
      return `${field}:${String(value).trim().toLowerCase()}`;
    }
  }
  return null;
}

function mergeViewTrees(trees: PermissionView[][]): PermissionView[] {
  const result: PermissionView[] = [];
  const indexes = new Map<string, number>();

  for (const tree of trees) {
    for (const view of tree) {
      const identity = viewIdentity(view);
      const existingIndex = identity ? indexes.get(identity) : undefined;

      if (existingIndex === undefined) {
        if (identity) indexes.set(identity, result.length);
        result.push(view);
        continue;
      }

      const existing = result[existingIndex];
      result[existingIndex] = {
        ...existing,
        ...view,
        permission: [...new Set([...existing.permission, ...view.permission])],
        subview: mergeViewTrees([existing.subview, view.subview]),
      };
    }
  }

  return result;
}

function normalizeViewTree(value: unknown, nowMs: number): PermissionView[] {
  return asArray(value)
    .map((view) => normalizeView(view, nowMs))
    .filter((view): view is PermissionView => view !== null);
}

function viewsFromRoleViews(value: unknown, nowMs: number): PermissionView[] {
  const trees = asArray(value).map((roleView) => {
    const record = asRecord(roleView);

    // Some endpoints return roleViews as wrappers; others return view nodes.
    return record && Array.isArray(record['views'])
      ? normalizeViewTree(record['views'], nowMs)
      : normalizeViewTree([roleView], nowMs);
  });

  return mergeViewTrees(trees);
}

function legacyPermissionTree(
  permissions: UnknownRecord,
  activeEstablishmentId: number | null,
  nowMs: number,
): PermissionView[] {
  const globalPermissions = asRecord(permissions['global']);
  const globalViews = viewsFromRoleViews(globalPermissions?.['roleViews'], nowMs);
  const establishments = asArray(permissions['establishments']);

  const activeEstablishment = activeEstablishmentId === null
    ? establishments[0]
    : establishments.find((value) => {
      const establishment = asRecord(value);
      return String(establishment?.['establishmentId']) === String(activeEstablishmentId);
    });

  const establishmentViews = viewsFromRoleViews(
    asRecord(activeEstablishment)?.['roleViews'],
    nowMs,
  );

  return mergeViewTrees([globalViews, establishmentViews]);
}

/**
 * Produces the single view-tree shape used throughout the UI. It accepts a raw
 * response, response.data, a permissionsV2 array, or the legacy permissions
 * object used by EstablishmentAdmin.
 */
export function normalizePermissionTree(
  source: unknown,
  activeEstablishmentId: number | null = null,
  nowMs = Date.now(),
): PermissionView[] {
  if (Array.isArray(source)) {
    return normalizeViewTree(source, nowMs);
  }

  const sourceRecord = asRecord(source);
  if (!sourceRecord) return [];

  const data = asRecord(sourceRecord['data']) ?? sourceRecord;
  const permissionsV2 = data['permissionsV2'];
  if (Array.isArray(permissionsV2) && permissionsV2.length) {
    return normalizeViewTree(permissionsV2, nowMs);
  }

  const legacyPermissions = asRecord(data['permissions'])
    ?? (data['global'] || data['establishments'] ? data : null);

  if (legacyPermissions) {
    return legacyPermissionTree(legacyPermissions, activeEstablishmentId, nowMs);
  }

  return Array.isArray(permissionsV2) ? [] : normalizeViewTree(data, nowMs);
}

export function parseStoredPermissionTree(
  stored: string | null,
  activeEstablishmentId: number | null = null,
  nowMs = Date.now(),
): PermissionView[] {
  if (!stored) return [];

  try {
    return normalizePermissionTree(
      JSON.parse(stored),
      activeEstablishmentId,
      nowMs,
    );
  } catch {
    return [];
  }
}

/**
 * Stores only the raw permission payload needed by the UI. In particular, it
 * retains validUntil metadata so each later read can evaluate expiry again.
 */
export function serializePermissionSource(source: unknown): string {
  if (Array.isArray(source)) return JSON.stringify(source);

  const sourceRecord = asRecord(source);
  if (!sourceRecord) return '[]';

  const data = asRecord(sourceRecord['data']) ?? sourceRecord;
  const storedSource: UnknownRecord = {};

  if (Array.isArray(data['permissionsV2'])) {
    storedSource['permissionsV2'] = data['permissionsV2'];
  }

  const permissions = asRecord(data['permissions']);
  if (permissions) {
    storedSource['permissions'] = permissions;
  } else if (data['global'] !== undefined || data['establishments'] !== undefined) {
    storedSource['permissions'] = data;
  }

  return Object.keys(storedSource).length
    ? JSON.stringify(storedSource)
    : '[]';
}
